/**
 * Services Slider
 *
 * Handles the slider/carousel functionality for the services block
 */

interface SliderState {
  currentIndex: number;
  slidesPerView: number;
  totalSlides: number;
  isDragging: boolean;
  startPos: number;
  currentTranslate: number;
  prevTranslate: number;
  animationID: number;
  hasMoved: boolean;
}

class ServicesSlider {
  private container: HTMLElement;
  private track: HTMLElement;
  private slides: NodeListOf<HTMLElement>;
  private prevBtn: HTMLButtonElement | null;
  private nextBtn: HTMLButtonElement | null;
  private dots: NodeListOf<HTMLButtonElement>;
  private state: SliderState;
  private autoplayInterval: number | null = null;
  private autoplay: boolean;
  private autoplayDelay: number;
  private isTransitioning = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.track = container.querySelector(
      '.services-slider__track'
    ) as HTMLElement;
    this.slides = container.querySelectorAll('.services-slider__slide');
    this.prevBtn = container.querySelector('.services-slider__nav--prev');
    this.nextBtn = container.querySelector('.services-slider__nav--next');
    this.dots = container.querySelectorAll('.services-slider__dot');

    // Read autoplay settings from data attributes
    this.autoplay = this.container.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(
      this.container.dataset.autoplayDelay || '5000'
    );

    this.state = {
      currentIndex: 0,
      slidesPerView: this.getSlidesPerView(),
      totalSlides: this.slides.length,
      isDragging: false,
      startPos: 0,
      currentTranslate: 0,
      prevTranslate: 0,
      animationID: 0,
      hasMoved: false,
    };

    this.init();
  }

  private init(): void {
    // Clone slides for infinite loop effect
    this.cloneSlidesForInfiniteLoop();

    // Set initial position to first real slide (after clones)
    this.state.currentIndex = this.state.totalSlides;
    this.setPositionByIndex();

    // Set up event listeners
    this.prevBtn?.addEventListener('click', () => this.prevSlide());
    this.nextBtn?.addEventListener('click', () => this.nextSlide());

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Touch/drag events
    this.track.addEventListener('mousedown', this.touchStart.bind(this));
    this.track.addEventListener('touchstart', this.touchStart.bind(this), {
      passive: false,
    });
    this.track.addEventListener('mouseup', this.touchEnd.bind(this));
    this.track.addEventListener('touchend', this.touchEnd.bind(this));
    this.track.addEventListener('mousemove', this.touchMove.bind(this));
    this.track.addEventListener('touchmove', this.touchMove.bind(this), {
      passive: false,
    });
    this.track.addEventListener('mouseleave', this.touchEnd.bind(this));

    // Prevent click on links if user has dragged
    this.track.addEventListener('click', this.handleClick.bind(this), true);

    // Keyboard navigation
    this.container.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') this.prevSlide();
      if (e.key === 'ArrowRight') this.nextSlide();
    });

    // Resize handler
    window.addEventListener(
      'resize',
      this.debounce(() => {
        this.state.slidesPerView = this.getSlidesPerView();
        // Recalculate position with new dimensions
        const slideWidth = this.slides[0]?.offsetWidth || 0;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
        const offset = -(this.state.currentIndex * (slideWidth + gap));
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${offset}px)`;
        // Force reflow
        void this.track.offsetHeight;
        this.track.style.transition = '';
        this.resizeTitles();
      }, 250)
    );

    // Pause autoplay on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoplay());
    this.container.addEventListener('mouseleave', () => this.startAutoplay());

    // Start autoplay
    this.startAutoplay();

    // Initial position
    this.updateSliderPosition();

    // Resize titles to fit
    this.resizeTitles();
  }

  private getSlidesPerView(): number {
    const width = window.innerWidth;
    if (width <= 480) return 1;
    if (width <= 768) return 2;
    if (width <= 1200) return 3;
    return 4;
  }

  private cloneSlidesForInfiniteLoop(): void {
    // Clone all slides and append/prepend for seamless infinite loop
    const slidesArray = Array.from(this.slides);
    const beforeClones: HTMLElement[] = [];

    slidesArray.forEach(slide => {
      const cloneBefore = slide.cloneNode(true) as HTMLElement;
      const cloneAfter = slide.cloneNode(true) as HTMLElement;
      cloneBefore.classList.add('is-clone');
      cloneAfter.classList.add('is-clone');
      beforeClones.push(cloneBefore);
      this.track.appendChild(cloneAfter);
    });

    // Insert before clones in correct order (reverse the array to maintain order)
    beforeClones.reverse().forEach(clone => {
      this.track.insertBefore(clone, this.track.firstChild);
    });
  }

  private setPositionByIndex(): void {
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
    const offset = -(this.state.currentIndex * (slideWidth + gap));

    this.track.style.transition = 'none';
    this.track.style.transform = `translateX(${offset}px)`;
  }

  private getMaxIndex(): number {
    return Math.max(0, this.state.totalSlides - this.state.slidesPerView);
  }

  private nextSlide(): void {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.state.currentIndex++;
    this.updateSliderPosition();

    // Check if we've reached a cloned slide at the end
    setTimeout(() => {
      if (this.state.currentIndex >= this.state.totalSlides * 2) {
        this.state.currentIndex -= this.state.totalSlides;
        this.setPositionByIndex();
      }
      this.isTransitioning = false;
    }, 500);
  }

  private prevSlide(): void {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.state.currentIndex--;
    this.updateSliderPosition();

    // Check if we've reached a cloned slide at the beginning
    setTimeout(() => {
      if (this.state.currentIndex < this.state.totalSlides) {
        this.state.currentIndex += this.state.totalSlides;
        this.setPositionByIndex();
      }
      this.isTransitioning = false;
    }, 500);
  }

  private goToSlide(index: number): void {
    if (this.isTransitioning) return;

    // Adjust index to account for cloned slides
    this.state.currentIndex = this.state.totalSlides + index;
    this.updateSliderPosition();
  }

  private updateSliderPosition(): void {
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
    const offset = -(this.state.currentIndex * (slideWidth + gap));

    this.track.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    this.track.style.transform = `translateX(${offset}px)`;
    this.updateDots();
  }

  private updateDots(): void {
    // Calculate the actual slide index (accounting for clones)
    let actualIndex = this.state.currentIndex % this.state.totalSlides;
    if (actualIndex < 0) actualIndex += this.state.totalSlides;

    this.dots.forEach((dot, index) => {
      const isActive = index === actualIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive.toString());
    });
  }

  // Touch/Drag functionality
  private getPositionX(event: MouseEvent | TouchEvent): number {
    return event.type.includes('mouse')
      ? (event as MouseEvent).pageX
      : (event as TouchEvent).touches[0].clientX;
  }

  private touchStart(event: MouseEvent | TouchEvent): void {
    this.state.isDragging = true;
    this.state.hasMoved = false;
    this.state.startPos = this.getPositionX(event);

    // Store the current translate value
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
    this.state.prevTranslate = -(this.state.currentIndex * (slideWidth + gap));
    this.state.currentTranslate = this.state.prevTranslate;

    this.state.animationID = requestAnimationFrame(this.animation.bind(this));
    this.track.style.cursor = 'grabbing';
    this.stopAutoplay();

    // Prevent default for mouse events to avoid text selection
    if (event.type === 'mousedown') {
      event.preventDefault();
    }
  }

  private touchMove(event: MouseEvent | TouchEvent): void {
    if (!this.state.isDragging) return;

    const currentPosition = this.getPositionX(event);
    const diff = currentPosition - this.state.startPos;

    // Mark as moved if dragged more than 5px
    if (Math.abs(diff) > 5) {
      this.state.hasMoved = true;
    }

    this.state.currentTranslate = this.state.prevTranslate + diff;

    // Prevent default to avoid scrolling on touch devices
    if (event.type === 'touchmove') {
      event.preventDefault();
    }
  }

  private touchEnd(): void {
    if (!this.state.isDragging) return;

    this.state.isDragging = false;
    cancelAnimationFrame(this.state.animationID);

    const movedBy = this.state.currentTranslate - this.state.prevTranslate;
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
    const slideWithGap = slideWidth + gap;

    // Calculate how many slides we've moved based on drag distance
    const slidesMoved = Math.round(movedBy / slideWithGap);

    // Update current index based on drag direction and distance
    if (Math.abs(movedBy) > 50) {
      // Minimum drag threshold
      this.state.currentIndex -= slidesMoved;
    }

    this.updateSliderPosition();

    // Check bounds for infinite loop after transition
    setTimeout(() => {
      if (this.state.currentIndex >= this.state.totalSlides * 2) {
        this.state.currentIndex -= this.state.totalSlides;
        this.setPositionByIndex();
      } else if (this.state.currentIndex < this.state.totalSlides) {
        this.state.currentIndex += this.state.totalSlides;
        this.setPositionByIndex();
      }
    }, 500);

    this.track.style.cursor = 'grab';
    this.startAutoplay();
  }

  private animation(): void {
    if (this.state.isDragging) {
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${this.state.currentTranslate}px)`;
      requestAnimationFrame(this.animation.bind(this));
    }
  }

  // Prevent click if user has dragged
  private handleClick(event: MouseEvent): void {
    if (this.state.hasMoved) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // Autoplay functionality
  private startAutoplay(): void {
    this.stopAutoplay();
    if (this.autoplay) {
      this.autoplayInterval = window.setInterval(() => {
        this.nextSlide();
      }, this.autoplayDelay);
    }
  }

  private stopAutoplay(): void {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  // Utility
  private debounce(
    func: (...args: unknown[]) => void,
    wait: number
  ): (...args: unknown[]) => void {
    let timeout: number | null = null;
    return (...args: unknown[]) => {
      if (timeout) clearTimeout(timeout);
      timeout = window.setTimeout(() => func(...args), wait);
    };
  }

  private resizeTitles(): void {
    const titles = this.container.querySelectorAll('.services-slider__title');
    titles.forEach(title => {
      const element = title as HTMLElement;
      let fontSize = parseFloat(getComputedStyle(element).fontSize);
      const minFontSize = 12;

      // Reset to computed size first
      element.style.fontSize = '';

      // Reduce font size until it fits
      while (
        element.scrollWidth > element.clientWidth &&
        fontSize > minFontSize
      ) {
        fontSize -= 1;
        element.style.fontSize = fontSize + 'px';
      }
    });
  }

  // Public method to destroy the slider
  public destroy(): void {
    this.stopAutoplay();
    // Remove event listeners if needed
  }
}

// Initialize all sliders on the page
function initServicesSliders(): void {
  const sliders = document.querySelectorAll<HTMLElement>('.services-slider');
  sliders.forEach(slider => {
    new ServicesSlider(slider);
  });
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initServicesSliders);
} else {
  initServicesSliders();
}

// Re-initialize on dynamic content load (for block editor preview)
if (typeof window !== 'undefined') {
  (
    window as unknown as { initServicesSliders: typeof initServicesSliders }
  ).initServicesSliders = initServicesSliders;
}

export { ServicesSlider, initServicesSliders };
